(ns ui.view.transaction-process.modals.decision.model
  "Pure state helpers for the decision modal. The form draft is an entity graph
  (decision + participants + conditions + reservations keyed by temp/real id)
  living under the TP state-path at `[:ui :modal-form]`."
  (:require
   [clojure.set :as set]
   [common.localization :refer [tr]]))

(defn section-config
  "Canonical section order. `sections` (when non-empty) restricts which sections
  render — driven by the decision type's `:decision-type/sections`. nil/empty =
  show all (backwards compatible with templates that don't configure sections)."
  [sections]
  (let [defs    [{:id :details      :label (tr :details)}
                 {:id :participants :label (tr :participants)}
                 {:id :conditions   :label (tr :conditions)}
                 {:id :reservations :label (tr :investment-reservations)}
                 {:id :notes        :label (tr :notes)}]
        by-id   (into {} (map (fn [{:keys [id] :as section}] [id section]) defs))]
    (if (seq sections)
      (vec (keep by-id sections))
      defs)))

(defn section-allowed?
  "Whether a section renders for this decision type. nil/empty `sections` = all
  sections allowed (templates that don't restrict)."
  [sections section-id]
  (or (empty? sections) (contains? (set sections) section-id)))

(defn build-participant-entries
  "Pure. Build [temp-id participant-entity] pairs from default participant ids,
  keeping only currently-active accounts (per the injected `active-account?`).
  Assigns a contiguous `:common/ord` so the seeded participants keep a stable
  order (matching how manual adds extend the ord)."
  [{:keys [default-participant-ids active-account? create-temp-id]}]
  (->> (or default-participant-ids [])
       (keep (fn [m]
               (let [account-id (if (map? m) (:value m) m)]
                 (when (and account-id (active-account? account-id))
                   account-id))))
       (map-indexed
        (fn [ord account-id]
          (let [temp-id (create-temp-id)]
            [temp-id {:db/id                           temp-id
                      :entity/type                     :entity.type/decision-participant
                      :decision-participant/person     account-id
                      :decision-participant/attendance :decision-participant.attendance/attended
                      :common/ord                      ord}])))
       vec))

(defn init-create-state
  "Pure. `participant-entries`/`reservation-entries` are [id entity] pairs the
  caller has already built (participants gated on the :participants section,
  reservations seeded from prefill). `slot-kw` is the TP named ref this decision
  links to (sent as `:slot-attr` on create)."
  [{:keys [decision-id entity-type slot-kw decision-type-label
           participant-entries reservation-entries default-date]}]
  (let [participant-ids (mapv first participant-entries)
        reservation-ids (mapv first reservation-entries)]
    {:edit-mode?          false
     :decision-id         decision-id
     :slot-kw             slot-kw
     :decision-type-label decision-type-label
     :submitting?         false
     :error               nil
     :entities            (merge
                           {decision-id (cond-> {:db/id           decision-id
                                                 :entity/type     entity-type
                                                 :decision/draft? true}
                                          default-date
                                          (assoc :decision/date default-date)
                                          (seq participant-ids)
                                          (assoc :decision/participants participant-ids)
                                          (seq reservation-ids)
                                          (assoc :decision/investment-reservations reservation-ids))}
                           (into {} participant-entries)
                           (into {} reservation-entries))}))

(defn init-edit-state
  "Pure. `entities` is the resolved id→entity map (decision + sub-entities) with
  the decision entity's ref lists already normalized to id vectors."
  [{:keys [decision-id slot-kw decision-type-label entities]}]
  {:edit-mode?          true
   :decision-id         decision-id
   :slot-kw             slot-kw
   :decision-type-label decision-type-label
   :submitting?         false
   :error               nil
   :entities            entities
   :original-entities   entities})

(defn compute-update-diff
  "Compare original vs current entities to find sub-entities added (string temp
  ids not present originally) and existing ones marked disabled."
  [{:keys [original-entities entities decision-id]}]
  (let [original-keys (set (keys (dissoc original-entities decision-id)))
        current-keys  (->> (keys (dissoc entities decision-id))
                           (remove (fn [k] (get-in entities [k :common/disabled?])))
                           set)
        added-ids     (->> (set/difference current-keys original-keys)
                           (filter string?)
                           set)
        disabled-ids  (->> (keys entities)
                           (filter (fn [k] (and (get-in entities [k :common/disabled?])
                                                (int? k))))
                           set)]
    {:added-entity-ids    added-ids
     :disabled-entity-ids disabled-ids}))

(defn next-ord
  "Next `:common/ord` so a freshly added sub-entity sorts after every existing
  one. Coalesces nil (legacy rows without an ord) to 0; empty -> 0."
  [entities]
  (->> entities
       (map (fn [e] (or (:common/ord e) 0)))
       (reduce max -1)
       inc))

(def default-min-width-by-data-type
  "Minimum usable width (px) per field data-type. Drives the flex-wrap layout: a
  field (or sub-group) wraps to the next line rather than shrinking below this,
  so e.g. a large currency amount is never truncated. Templates can override per
  field via :field/min-width."
  {:data.type/currency-value 320
   :data.type/date           220
   :data.type/text-simple    200
   :data.type/text-multiline 200})

(def reservation-group-gap-px
  "Horizontal gap between sibling fields/sub-groups (matches gap-x-3 = 0.75rem).
  Used to size a sub-group's flex-basis so it wraps as a block at the right point."
  12)

(defn reservation-node-min-width
  "Preferred width (px) of a layout node. A leaf uses its data-type min-width (or
  :field/min-width override); a sub-group sums its children plus the gaps between
  them, so the whole group only sits beside a sibling when there's room for all of
  it and otherwise wraps as a block."
  [node]
  (if (vector? node)
    (+ (reduce + (map reservation-node-min-width node))
       (* reservation-group-gap-px (max 0 (dec (count node)))))
    (or (:field/min-width node)
        (get default-min-width-by-data-type (:field/data-type node) 200))))

(defn reservation-node-grow
  "Flex-grow weight: a leaf's :field/grow (default 1); a sub-group is the sum of
  its children so a row of equal-grow leaves stays evenly proportioned."
  [node]
  (if (vector? node)
    (reduce + (map reservation-node-grow node))
    (or (:field/grow node) 1)))

(defn reservation-node-style
  [node]
  (let [mw (reservation-node-min-width node)]
    {:flex-grow   (reservation-node-grow node)
     :flex-shrink 1
     :flex-basis  (str mw "px")
     ;; Cap at 100% so a wide field/group never overflows when the card is
     ;; narrower than its preferred width — it shrinks and wraps internally.
     :min-width   (str "min(100%, " mw "px)")}))

(defn filter-reservation-layout
  "Drop fields the reservation type disables, then remove emptied sub-groups and
  rows so the layout collapses cleanly around hidden fields."
  [enabled? layout]
  (->> layout
       (map (fn [row]
              (->> row
                   (keep (fn [node]
                           (if (vector? node)
                             (let [kept (filterv #(enabled? (:field/attribute %)) node)]
                               (when (seq kept) kept))
                             (when (enabled? (:field/attribute node)) node))))
                   vec)))
       (filter seq)
       vec))
